/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { FrequencyStore } from '../../../activity/frequency';
import { Opportunity } from '../../../auction';
import { Logger } from '../../../logger';
import { AdIdType, CampaignType } from '../../../types';
import { generateUUID } from '../../../util';
import { extractClickThroughDomain } from './clickthrough';
import {
  PlacementType,
  VIEWABILITY_CHECK_INTERVAL_MS,
  VIEWABLE_IMPRESSION_PLAYBACK_MS,
} from '../constants';
import { Placement } from '../index';

const logger = Logger.getInstance();

enum Quartile {
  FIRST = 1,
  SECOND = 2,
  THIRD = 3,
  FOURTH = 4,
}

abstract class BaseVideoPlacement extends Placement {
  // True while an auction-minted opportunity is queued but not yet claimed by an
  // ad start. The first ad after an auction claims it (preserving the
  // auction<->impression join); subsequent ads in the same break/pod mint their
  // own. A boolean is used rather than array-index arithmetic because the
  // opportunity queue is capped (MAX_STORED_OPPORTUNITIES) and shifts, which
  // would otherwise drift the claim cursor.
  private _hasUnclaimedAuctionOpportunity: boolean = false;
  protected _playbackOpportunity: Opportunity | null = null;

  protected playerWidth: number = 0;
  protected playerHeight: number = 0;

  // Per-opportunity tracking: Maps opportunity ID to quartiles fired
  protected quartilesFiredByOpportunity: Map<string, Set<Quartile>> = new Map();
  // Per-opportunity tracking: Set of opportunity IDs that have completed
  protected completedOpportunityIds: Set<string> = new Set();
  protected adCurrentlyPlaying: boolean = false;

  private viewabilityIntervalId: ReturnType<typeof setInterval> | null = null;
  private continuouslyInViewSinceMs: number | null = null;
  private viewabilityPaused: boolean = false;

  public get currentOpportunity(): Opportunity | null {
    return (
      this._playbackOpportunity ??
      (this.opportunities.length > 0 ? this.opportunities[this.opportunities.length - 1] : null)
    );
  }

  public get placementType(): PlacementType {
    return PlacementType.VIDEO;
  }

  public newOpportunity(opportunity: Opportunity): void {
    super.newOpportunity(opportunity);
    // A fresh auction opportunity is available for the next ad start to claim.
    this._hasUnclaimedAuctionOpportunity = true;
  }

  protected adStarted(): void {
    // Every video ad start is a new render: the per-ad (render-bound) state —
    // impression context, interaction latch, accidental-click detector — must
    // reset here. newOpportunity() no longer performs this reset (it is an
    // auction event, not a render event), so this call preserves the per-ad
    // semantics for pods/auto-next that the opportunity handling below relies
    // on.
    this.resetStateForNewRender();
    // Each ad that starts must own a distinct opportunity. The first ad after a
    // Prebid auction claims the auction-minted opportunity (preserving the
    // auction<->impression join). A subsequent ad with no new auction in between
    // (a later ad in the same break/pod, or an auto-next preroll) mints a fresh
    // opportunity and resets per-ad state. Without this, follow-on ads reuse the
    // prior ad's opportunity, suppressing their impression while re-firing
    // quartiles (observed as >100% completion on pod/auto-next inventory).
    if (this._hasUnclaimedAuctionOpportunity) {
      // Claim the queued auction opportunity (the latest one in the queue).
      this._playbackOpportunity = this.opportunities[this.opportunities.length - 1] ?? null;
      this._hasUnclaimedAuctionOpportunity = false;
    } else {
      // No unclaimed auction opportunity — mint a follow-on. newOpportunity()
      // runs the full per-opportunity reset and queues + sets the flag, which we
      // immediately claim here.
      this.newOpportunity(new Opportunity(generateUUID()));
      this._playbackOpportunity = this.opportunities[this.opportunities.length - 1] ?? null;
      this._hasUnclaimedAuctionOpportunity = false;
      // A follow-on ad's opportunity is minted locally with no auction to
      // freeze page signals against; snapshot them at ad start instead so
      // its impression carries xd like any other tracked render.
      FrequencyStore.getInstance().captureRenderPageSignals(this._playbackOpportunity?.id);
    }

    this.adCurrentlyPlaying = true;
    this.startViewabilityTracking();
  }

  /**
   * The element whose on-screen area defines whether the player is in view.
   * Subclasses override when the visible player is not the slot itself (e.g.
   * Dailymotion docks a child wrapper while the slot stays in document flow).
   */
  protected getViewabilityElement(): HTMLElement | null {
    return this.slot;
  }

  protected isPlayerInView(): boolean {
    const element = this.getViewabilityElement();
    if (!element) {
      return false;
    }
    const width = element.clientWidth || this.playerWidth;
    const height = element.clientHeight || this.playerHeight;
    if (!width || !height) {
      return false;
    }
    return this.checkElementVisibility(element, { width, height });
  }

  private startViewabilityTracking(): void {
    this.stopViewabilityTracking();
    this.viewabilityPaused = false;
    this.viewabilityIntervalId = setInterval(
      () => this.viewabilityTick(),
      VIEWABILITY_CHECK_INTERVAL_MS,
    );
    this.viewabilityTick();
  }

  private stopViewabilityTracking(): void {
    if (this.viewabilityIntervalId !== null) {
      clearInterval(this.viewabilityIntervalId);
      this.viewabilityIntervalId = null;
    }
    this.continuouslyInViewSinceMs = null;
  }

  /**
   * Playback interruptions must not count towards the continuous in-view
   * window — a viewable impression can neither span a pause nor accumulate
   * while paused.
   */
  protected pauseViewabilityTracking(): void {
    this.viewabilityPaused = true;
    this.continuouslyInViewSinceMs = null;
  }

  protected resumeViewabilityTracking(): void {
    this.viewabilityPaused = false;
  }

  private viewabilityTick(): void {
    if (!this.adCurrentlyPlaying) {
      this.stopViewabilityTracking();
      return;
    }
    if (this.viewabilityPaused || !this.isPlayerInView()) {
      this.continuouslyInViewSinceMs = null;
      return;
    }
    const now = Date.now();
    this.continuouslyInViewSinceMs ??= now;
    if (now - this.continuouslyInViewSinceMs >= VIEWABLE_IMPRESSION_PLAYBACK_MS) {
      this.stopViewabilityTracking();
      this.dispatchViewableImpression();
    }
  }

  protected setSlot(): void {
    if (this.slot !== null) {
      return;
    }

    const adUnitVideoConfig = this.adUnit?.video;
    if (!adUnitVideoConfig?.divId) {
      logger.warn(`No divId found in adUnit video config for ${this.adUnitCode}`);
      return;
    }

    const slotElement = document.getElementById(adUnitVideoConfig.divId);
    if (!slotElement) {
      logger.warn(`No slot element found for divId ${adUnitVideoConfig.divId}`);
      return;
    }

    this.slot = slotElement;
  }

  protected setWrappingDomElement(): void {
    if (this.slot) {
      this.wrappingDomElement = this.slot;
      this.playerWidth = this.slot.clientWidth;
      this.playerHeight = this.slot.clientHeight;
    }
  }

  protected resetStateForNewOpportunity(): void {
    // Majority of video ad flags are tracked by opportunity ID, so no need to reset here.
    this.adCurrentlyPlaying = false;
    this.stopViewabilityTracking();
  }

  protected determineVisibility(): void {
    this.isVisible(this.isPlayerInView());
  }

  public destroy(): void {
    this.stopViewabilityTracking();
    super.destroy();
  }

  /**
   * Records an exchange-bought ad identified only by its clickthrough domain.
   *
   * Shared by every IMA-backed player: an ad that fails the direct-sold gate
   * has no GAM-resolvable creative id, but its click chain often identifies the
   * buyer, and an advertiser domain is all a programmatic capture needs.
   *
   * The capture carries **no creative id** — `ProgrammaticServedAdImpression`
   * has no field for one, and a bidder or exchange creative id cannot be
   * resolved against the ad server, so recording it would imply a link that
   * does not exist.
   *
   * A misparsed chain surfaces a click tracker (e.g. `6sc.co`) as the
   * advertiser. That is recoverable downstream — brand classification filters
   * known ad-platform hostnames and can flag the rest `exclude_from_training` —
   * unlike attaching a real advertiser's domains to an ad they never bought.
   *
   * @returns true when an impression was dispatched.
   */
  protected captureExchangeAdByClickThrough(
    traffickingParametersString: string | null | undefined,
    durationSeconds: number,
  ): boolean {
    const clickThrough = extractClickThroughDomain(traffickingParametersString);
    if (!clickThrough) {
      return false;
    }

    const advertiserDomains = this.cleanAdvertiserDomains([clickThrough.domain]);
    if (advertiserDomains.length === 0) {
      return false;
    }

    logger.debug(
      `Exchange ad for ${this.adUnitCode} identified as ${clickThrough.domain} via clickthrough chain [${clickThrough.chain.join(' -> ')}]`,
    );

    this.adCurrentlyPlaying = true;

    this.renderedAdData = {
      advertiserDomains,
      durationSeconds: durationSeconds > 0 ? Math.round(durationSeconds) : undefined,
      isPrebidAd: false,
      isProgrammaticBuy: true,
      responseIdentifier: generateUUID(),
      size: { width: this.playerWidth, height: this.playerHeight },
    };

    const deliveryId = this.getLearningCampaignDeliveryId(CampaignType.LEARNING_PROGRAMMATIC);
    if (!deliveryId) {
      return false;
    }
    this.dispatchProgrammaticServedAdImpression(deliveryId);
    return true;
  }

  /**
   * Advertiser domains recoverable from a direct-classified ad's click chain.
   *
   * A pass-through creative (a VAST redirect wrapping exchange demand — A9,
   * Prebid-video cache redirects) passes the direct-sold gate, but its
   * creative id identifies the pipe, not the buyer. When the served VAST
   * still carries a walkable click chain (verified live for the Prebid
   * video-Index Exchange pass-through: Alton Towers, John Lewis, giffgaff),
   * the buyer's domain can be recorded ALONGSIDE the creative id and the
   * server decides which to trust. A9 pass-throughs carry no clickthrough at
   * all (also verified live), so they simply yield nothing here.
   */
  protected clickThroughAdvertiserDomains(
    traffickingParametersString: string | null | undefined,
  ): string[] | undefined {
    const clickThrough = extractClickThroughDomain(traffickingParametersString);
    if (!clickThrough) {
      return undefined;
    }
    const advertiserDomains = this.cleanAdvertiserDomains([clickThrough.domain]);
    return advertiserDomains.length > 0 ? advertiserDomains : undefined;
  }

  protected dispatchAdQuartileEvent(quartile: Quartile): void {
    const currentOpportunity = this.currentOpportunity;
    if (!currentOpportunity) {
      return;
    }

    const opportunityId = currentOpportunity.id;

    if (this.completedOpportunityIds.has(opportunityId)) {
      return;
    }

    const firedQuartiles = this.quartilesFiredByOpportunity.get(opportunityId);
    if (firedQuartiles && firedQuartiles.has(quartile)) {
      return;
    }

    if (!this.getImpressionDeliveryId()) {
      return;
    }

    if (!this.quartilesFiredByOpportunity.has(opportunityId)) {
      this.quartilesFiredByOpportunity.set(opportunityId, new Set());
    }
    this.quartilesFiredByOpportunity.get(opportunityId)!.add(quartile);

    if (quartile === Quartile.FOURTH) {
      this.completedOpportunityIds.add(opportunityId);
    }

    this.dispatch('VideoAdPlaybackQuartileReached', {
      quartile: quartile,
      impressionId: this.renderedAdData?.responseIdentifier ?? generateUUID(),
      opportunityId: opportunityId,
      deliveryId: this.getImpressionDeliveryId() as string,
      advertiserId: this.renderedAdData?.advertiserId ?? undefined,
      advertiserDomains: this.renderedAdData?.advertiserDomains ?? undefined,
      lineItemId: this.renderedAdData?.lineItemId ?? undefined,
      nodalsDealId: this.renderedAdData?.nodalsDealId ?? undefined,
      selectionLabel: this.getSelectionLabel(this.getImpressionDeliveryId()!, AdIdType.DELIVERY_ID),
      t: this.isLearningCampaignDeliveryId(this.getImpressionDeliveryId()!) ? 1 : 0,
    });
  }
}

export { BaseVideoPlacement, Quartile };
